import Avatar from "@/avatar";
import { optionTypes } from "@/avatar/options/types";
import PieceGrid from "@/components/PieceGrid";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { getUserID } from "@/lib/auth/auth-server-func";
import { authClient } from "@/lib/better-auth/client";
import MainLayout from "@/lib/layout/MainLayout";
import { useCreateAvatar } from "@/lib/tanstack-query/hooks/useCreateAvatar";
import { useQueryClient } from "@tanstack/react-query";
import { createFileRoute, redirect } from "@tanstack/react-router";
import { Loader2 } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

export type Avatar = {
  topType?: string;
  hairColor?: string;
  hatColor?: string;
  facialHairType?: string;
  facialHairColor?: string;
  accessoriesType?: string;
  mouthType?: string;
  skinColor?: string;
  eyebrowType?: string;
  eyeType?: string;
  clotheType?: string;
  clotheColor?: string;
  graphicType?: string;
};

export const Route = createFileRoute("/_dashboard/avatar")({
  component: RouteComponent,
  beforeLoad: async ({ context }) => {
    const userID = await getUserID();
    return { userID };
  },
  loader: async ({ context }) => {
    if (!context.userID) {
      throw redirect({ to: "/" });
    }
    return { userID: context.userID };
  },
});

function RouteComponent() {
  const queryClient = useQueryClient();
  const [loading, setLoading] = useState(true);
  const { mutateAsync: createAvatar, isPending: isPendingCreateAvatar } =
    useCreateAvatar();
  const {
    data: userSession,
    refetch,
    isPending: isPendingSession,
  } = authClient.useSession();
  const parsedAvatar: Avatar | null = useMemo(() => {
    return typeof userSession?.user?.avatar === "string"
      ? JSON.parse(userSession.user.avatar)
      : null;
  }, [userSession?.user?.avatar]);

  const [currentPieceType, setCurrentPieceType] = useState("topType");
  const [topCategory, setTopCategory] = useState<string | undefined>("hair");
  const [hatColor, setHatColor] = useState<string | undefined>(undefined);
  const [hairColor, setHairColor] = useState<string | undefined>(undefined);
  const [facialHairColor, setFacialHairColor] = useState<string | undefined>(
    undefined
  );
  const [clotheColor, setClotheColor] = useState<string | undefined>(undefined);

  const [selectedPieces, setSelectedPieces] = useState<
    | {
        topType?: string;
        facialHairType?: string;
        accessoriesType?: string;
        mouthType?: string;
        skinColor?: string;
        eyebrowType?: string;
        eyeType?: string;
        clotheType?: string;
        graphicType?: string;
      }
    | undefined
  >(undefined);

  const handleSelectionChange = (type: string, value: string) => {
    setSelectedPieces((prev) => ({
      ...prev,
      [type]: value,
    }));
  };

  const saveAvatar = async () => {
    await createAvatar({
      topType: selectedPieces?.topType,
      hairColor: hairColor,
      accessoriesType: selectedPieces?.accessoriesType,
      clotheColor: clotheColor,
      clotheType: selectedPieces?.clotheType,
      eyeType: selectedPieces?.eyeType,
      eyebrowType: selectedPieces?.eyebrowType,
      graphicType: selectedPieces?.graphicType,
      facialHairColor: facialHairColor,
      facialHairType: selectedPieces?.facialHairType,
      hatColor: hatColor,
      mouthType: selectedPieces?.mouthType,
      skinColor: selectedPieces?.skinColor,
    });
    await refetch();
  };

  useEffect(() => {
    setHatColor(parsedAvatar?.hatColor || "Black");
    setHairColor(parsedAvatar?.hairColor || "Black");
    setFacialHairColor(parsedAvatar?.facialHairColor || "Black");
    setClotheColor(parsedAvatar?.clotheColor || "Black");
    setSelectedPieces({
      topType: parsedAvatar?.topType || "LongHairBob",
      facialHairType: parsedAvatar?.facialHairType || "Blank",
      accessoriesType: parsedAvatar?.accessoriesType || "Blank",
      mouthType: parsedAvatar?.mouthType || "Default",
      skinColor: parsedAvatar?.skinColor || "Light",
      eyebrowType: parsedAvatar?.eyebrowType || "Default",
      eyeType: parsedAvatar?.eyeType || "Default",
      clotheType: parsedAvatar?.clotheType || "CollarSweater",
      graphicType: parsedAvatar?.graphicType || "Bat",
    });

    setLoading(false);
  }, [parsedAvatar]);

  if (loading) return null;

  return (
    <>
      <MainLayout.Header headline="Avatar"></MainLayout.Header>
      <MainLayout.Content>
        <div className="flex flex-col items-center">
          <div className="bg-background sticky top-[73px] md:top-0 z-10 w-full">
            <div className="relative flex flex-col items-center gap-4">
              <Button
                type="button"
                onClick={saveAvatar}
                className="absolute top-0 right-0 text-base font-semibold"
              >
                {isPendingCreateAvatar || isPendingSession ? (
                  <>
                    <Loader2 className="animate-spin" />
                    Loading...
                  </>
                ) : (
                  "Save"
                )}
              </Button>

              <Avatar
                className="avatar"
                avatarStyle="Circle"
                topType={selectedPieces?.topType as optionTypes["topType"]}
                accessoriesType={
                  selectedPieces?.accessoriesType as optionTypes["accessoriesType"]
                }
                facialHairType={
                  selectedPieces?.facialHairType as optionTypes["facialHairType"]
                }
                hatColor={hatColor as optionTypes["hatColor"]}
                hairColor={hairColor as optionTypes["hairColor"]}
                facialHairColor={
                  facialHairColor as optionTypes["facialHairColor"]
                }
                mouthType={
                  selectedPieces?.mouthType as optionTypes["mouthType"]
                }
                skinColor={
                  selectedPieces?.skinColor as optionTypes["skinColor"]
                }
                eyebrowType={
                  selectedPieces?.eyebrowType as optionTypes["eyebrowType"]
                }
                eyeType={selectedPieces?.eyeType as optionTypes["eyeType"]}
                clotheType={
                  selectedPieces?.clotheType as optionTypes["clotheType"]
                }
                clotheColor={clotheColor as optionTypes["clotheColor"]}
                graphicType={
                  selectedPieces?.graphicType as optionTypes["graphicType"]
                }
              />

              <div className="flex w-full gap-4 dark:text-white">
                <Select
                  value={currentPieceType}
                  onValueChange={setCurrentPieceType}
                >
                  <SelectTrigger className="flex-1">
                    <SelectValue placeholder="Select a piece type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="topType">Top</SelectItem>
                    <SelectItem value="facialHairType">Facial Hair</SelectItem>
                    <SelectItem value="mouthType">Mouth</SelectItem>
                    <SelectItem value="eyebrowType">Eyebrow</SelectItem>
                    <SelectItem value="eyeType">Eyes</SelectItem>
                    <SelectItem value="clotheType">Clothe</SelectItem>
                    <SelectItem value="skinColor">Skin Color</SelectItem>
                    <SelectItem value="accessoriesType">Accessories</SelectItem>
                  </SelectContent>
                </Select>
                {currentPieceType === "topType" && (
                  <Select value={topCategory} onValueChange={setTopCategory}>
                    <SelectTrigger className="flex-1">
                      <SelectValue placeholder="Select a top category" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="hair">Hair</SelectItem>
                      <SelectItem value="hat">Hat</SelectItem>
                    </SelectContent>
                  </Select>
                )}
                {currentPieceType === "topType" && topCategory === "hair" && (
                  <Select value={hairColor} onValueChange={setHairColor}>
                    <SelectTrigger className="flex-1">
                      <SelectValue placeholder="Select a hair color" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Black">Black</SelectItem>
                      <SelectItem value="Blonde">Blonde</SelectItem>
                      <SelectItem value="BlondeGolden">BlondeGolden</SelectItem>
                      <SelectItem value="Brown">Brown</SelectItem>
                      <SelectItem value="BrownDark">BrownDark</SelectItem>
                      <SelectItem value="PastelPink">PastelPink</SelectItem>
                      <SelectItem value="Blue">Blue</SelectItem>
                      <SelectItem value="Platinum">Platinum</SelectItem>
                      <SelectItem value="Red">Red</SelectItem>
                      <SelectItem value="SilverGray">SilverGray</SelectItem>
                    </SelectContent>
                  </Select>
                )}
                {currentPieceType === "topType" && topCategory === "hat" && (
                  <Select value={hatColor} onValueChange={setHatColor}>
                    <SelectTrigger className="flex-1">
                      <SelectValue placeholder="Select a hat color" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Black">Black</SelectItem>
                      <SelectItem value="Blue01">Blue01</SelectItem>
                      <SelectItem value="Blue02">Blue02</SelectItem>
                      <SelectItem value="Blue03">Blue03</SelectItem>
                      <SelectItem value="Gray01">Gray01</SelectItem>
                      <SelectItem value="Gray02">Gray02</SelectItem>
                      <SelectItem value="Heather">Heather</SelectItem>
                      <SelectItem value="PastelBlue">PastelBlue</SelectItem>
                      <SelectItem value="PastelGreen">PastelGreen</SelectItem>
                      <SelectItem value="PastelOrange">PastelOrange</SelectItem>
                      <SelectItem value="PastelRed">PastelRed</SelectItem>
                      <SelectItem value="PastelYellow">PastelYellow</SelectItem>
                      <SelectItem value="Pink">Pink</SelectItem>
                      <SelectItem value="Red">Red</SelectItem>
                      <SelectItem value="White">White</SelectItem>
                    </SelectContent>
                  </Select>
                )}

                {currentPieceType === "facialHairType" && (
                  <Select
                    value={facialHairColor}
                    onValueChange={setFacialHairColor}
                  >
                    <SelectTrigger className="flex-1">
                      <SelectValue placeholder="Select a facial hair color" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Auburn">Auburn</SelectItem>
                      <SelectItem value="Black">Black</SelectItem>
                      <SelectItem value="Blonde">Blonde</SelectItem>
                      <SelectItem value="BlondeGolden">BlondeGolden</SelectItem>
                      <SelectItem value="Brown">Brown</SelectItem>
                      <SelectItem value="BrownDark">BrownDark</SelectItem>
                      <SelectItem value="Platinum">Platinum</SelectItem>
                      <SelectItem value="Red">Red</SelectItem>
                      <SelectItem value="SilverGray">SilverGray</SelectItem>
                    </SelectContent>
                  </Select>
                )}
                {currentPieceType === "clotheType" && (
                  <Select value={clotheColor} onValueChange={setClotheColor}>
                    <SelectTrigger className="flex-1">
                      <SelectValue placeholder="Select a clothe color" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Black">Black</SelectItem>
                      <SelectItem value="Blue01">Blue01</SelectItem>
                      <SelectItem value="Blue02">Blue02</SelectItem>
                      <SelectItem value="Blue03">Blue03</SelectItem>
                      <SelectItem value="Gray01">Gray01</SelectItem>
                      <SelectItem value="Gray02">Gray02</SelectItem>
                      <SelectItem value="Heather">Heather</SelectItem>
                      <SelectItem value="PastelBlue">PastelBlue</SelectItem>
                      <SelectItem value="PastelGreen">PastelGreen</SelectItem>
                      <SelectItem value="PastelOrange">PastelOrange</SelectItem>
                      <SelectItem value="PastelRed">PastelRed</SelectItem>
                      <SelectItem value="PastelYellow">PastelYellow</SelectItem>
                      <SelectItem value="Pink">Pink</SelectItem>
                      <SelectItem value="Red">Red</SelectItem>
                      <SelectItem value="White">White</SelectItem>
                    </SelectContent>
                  </Select>
                )}
                {currentPieceType === "clotheType" &&
                  selectedPieces?.clotheType === "GraphicShirt" && (
                    <Select
                      value={selectedPieces.graphicType}
                      onValueChange={(value) => {
                        setSelectedPieces((prev) => ({
                          ...prev,
                          graphicType: value,
                        }));
                      }}
                    >
                      <SelectTrigger className="flex-1">
                        <SelectValue placeholder="Select a graphic" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Bat">Bat</SelectItem>
                        <SelectItem value="Cumbia">Cumbia</SelectItem>
                        <SelectItem value="Deer">Deer</SelectItem>
                        <SelectItem value="Diamond">Diamond</SelectItem>
                        <SelectItem value="Hola">Hola</SelectItem>
                        <SelectItem value="Pizza">Pizza</SelectItem>
                        <SelectItem value="Resist">Resist</SelectItem>
                        <SelectItem value="Selena">Selena</SelectItem>
                        <SelectItem value="Bear">Bear</SelectItem>
                        <SelectItem value="SkullOutline">
                          SkullOutline
                        </SelectItem>
                        <SelectItem value="Skull">Skull</SelectItem>
                      </SelectContent>
                    </Select>
                  )}
              </div>
            </div>
          </div>

          <div className="pt-6">
            <PieceGrid
              type={currentPieceType}
              topCategory={topCategory}
              color={
                currentPieceType === "topType"
                  ? topCategory === "hair"
                    ? hairColor
                    : topCategory === "hat"
                      ? hatColor
                      : undefined
                  : currentPieceType === "facialHairType"
                    ? facialHairColor
                    : currentPieceType === "clotheType"
                      ? clotheColor
                      : undefined
              }
              selected={
                selectedPieces?.[
                  currentPieceType as keyof typeof selectedPieces
                ]
              }
              onChangeSelected={(value) =>
                handleSelectionChange(currentPieceType, value)
              }
            />
          </div>
        </div>
      </MainLayout.Content>
    </>
  );
}
